import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useLocation } from 'react-router-dom';

import { announce, Button, useStickySidebar, useWindowSize } from 'sarsaparilla';

/**
 * Link click handler that smooths scrolls (if Chrome) to the link,
 * sets the focus, and updates the browser url
 */
function anchorLinkHandler(event, id, offsetTop) {
    const distanceToTop = (el) => Math.floor(el.getBoundingClientRect().top);
    const targetId = id || event?.target?.getAttribute('href');
    const targetAnchor = document.querySelector(targetId);
    if (!targetAnchor) return;

    let timer;

    /**
     * Check when scroll event ends
     */
    const onScrollEnd = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            targetAnchor.focus(); // Set the focus to first section element
            window.removeEventListener('scroll', onScrollEnd); // Remove listener after the element gets the focus
        }, 300);
    };

    announce(`Scrolled to ${targetAnchor?.innerText || id} section`); // no matter what, try to announce it as page has moved
    const originalTop = distanceToTop(targetAnchor);
    window.scrollBy({ top: originalTop - 100 - offsetTop, left: 0, behavior: 'smooth' });
    window.addEventListener('scroll', onScrollEnd); // Add scroll event listener
}

/**
 * Exported React component
 */
export default function AnchorPageWithSidebarLayout({ children, offsetTop = 0, ...rest }) {
    const notSidebarRef = useRef();
    // caching the scroll position so we can determine if the scroll direction is up or down
    const windowScrollY = useRef(window?.scrollY);

    const [activeLink, setActiveLink] = useState('');
    const [headings, setHeadings] = useState([]);
    const [headingElements, setHeadingElements] = useState([]);
    const [intersects, setIntersects] = useState({});

    const { hash } = useLocation();
    const { width } = useWindowSize();
    const { containerRef, stuckStatus, containerWidth } = useStickySidebar(
        80 + offsetTop,
        width > 990
    );

    // ---------------------------------------
    // Callback for IntersectionObserver
    function intersectionCallback(entries) {
        const entry = entries[0];

        setIntersects(prev => ({
            ...prev,
            [entry.target.id]: entry.intersectionRatio,
        }));
    }

    // ---------------------------------------
    // If there is a hash in the url, scroll the page to that id
    useEffect(() => {
        if (hash && !isEmpty(headingElements)) {
            anchorLinkHandler(null, hash, offsetTop);
        }
    }, [hash, headingElements]);

    // ---------------------------------------
    // When insects changes, check to see if the active link needs to change
    useEffect(() => {
        const currentScrollY = window?.scrollY;
        const isScrollingDown = currentScrollY >= windowScrollY.current;

        windowScrollY.current = currentScrollY;

        const currentActiveIndex = headings.indexOf(activeLink);
        const currentSection = `${headings[currentActiveIndex]}-section`;
        const nextSection = `${headings[currentActiveIndex + 1]}-section`;
        const previousSection =
            currentActiveIndex >= 0
                ? `${headings[currentActiveIndex - 1]}-section`
                : null;

        // Scrolling down (active section incrementing)
        if (
            isScrollingDown &&
            currentActiveIndex < headings.length &&
            intersects[currentSection] <= 0.25 &&
            intersects[nextSection] >= 0.25
        ) {
            setActiveLink(headings[currentActiveIndex + 1]);
        }

        // Scrolling up (active section decrementing)
        if (
            !isScrollingDown &&
            previousSection &&
            currentActiveIndex > 0 &&
            (intersects[previousSection] >= 0.25 ||
                intersects[previousSection] > intersects[currentSection])
        ) {
            setActiveLink(headings[currentActiveIndex - 1]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intersects]);

    // ---------------------------------------
    // Get all the headings and setup IntersectionObservers
    useEffect(() => {
        let isMounted = true;
        const observers = [];

        if (notSidebarRef?.current?.querySelectorAll) {
            const headingEls = Array.from(
                notSidebarRef.current.querySelectorAll(
                    '[data-shared-anchor-page-section-heading]'
                )
            );
            const headingIds = headingEls.map(item => item.id);

            if (isMounted) {
                setActiveLink(headingIds[0]);
                setHeadings(headingIds);
                setHeadingElements(headingEls);
            }

            if (!isEmpty(headingIds)) {
                headingIds.forEach(id => {
                    const options = {
                        rootMargin: '-90px 0px 0px 0px',
                        threshold: [0, 0.25, 0.5, 0.75, 1],
                    };
                    const observer = new IntersectionObserver(
                        intersectionCallback,
                        options
                    );
                    observer.observe(document.getElementById(`${id}-section`));
                    observers.push(observer);
                });
            }
        }

        return () => {
            isMounted = false;
            observers.forEach(observer => {
                observer.disconnect();
            });
        };
    }, [children]);

    return (
        <div>
            <div data-component="AnchorPageWithSidebarLayout" {...rest}>
                <div className="intermediary-wrapper">
                    <div className="sidebar">
                        <div className="sticky-container" ref={containerRef}>
                            {!isEmpty(headingElements) && (
                                <ul
                                    style={{ width: `${containerWidth}px` }}
                                    aria-label="Page contents"
                                    className={cx('shared-toc-list', {
                                        'stuck-top': stuckStatus === 'top',
                                        'stuck-bottom':
                                            stuckStatus === 'bottom',
                                    })}
                                >
                                    {headingElements.map(item => {
                                        return (
                                            <li key={item.id}>
                                                <Button
                                                    appearance="link"
                                                    className={cx('p-1', {
                                                        active:
                                                            item.id ===
                                                            activeLink,
                                                    })}
                                                    onClick={(ev) => anchorLinkHandler(ev, `#${item.id}`, offsetTop)}
                                                >
                                                    {item.innerHTML}
                                                </Button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div ref={notSidebarRef} className="not-sidebar">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

AnchorPageWithSidebarLayout.propTypes = {
    children: PropTypes.node,
    offsetTop: PropTypes.number
};
